import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Lock, User, Receipt, MapPin, Plus, Pencil, Trash2, ShieldCheck, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PSGCAddressDropdowns from "@/components/PSGCAddressDropdowns";

// ---------- OTP confirmation dialog ----------
const OtpDialog = ({ open, title, onClose, onSubmit, label = "Enter the 6-digit code" }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setCode(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="otp-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500">{label}</p>
        <input type="text" maxLength={6} inputMode="numeric"
          className="pav-input tracking-[0.5em] text-center text-2xl font-mono"
          data-testid="otp-input"
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus />
        <p className="text-[11px] text-neutral-400 italic">Demo: check the backend console for the OTP.</p>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
          <button onClick={async () => { setBusy(true); try { await onSubmit(code); } finally { setBusy(false); } }}
            disabled={busy || code.length !== 6} data-testid="otp-submit"
            className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Password Tab ----------
const PasswordTab = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [otpOpen, setOtpOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/me/request-password-otp", form);
      toast.success("Code sent — check your email");
      setOtpOpen(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const verify = async (code) => {
    try {
      await api.post("/me/change-password", { code, new_password: form.new_password });
      toast.success("Password updated successfully");
      setOtpOpen(false);
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid code"); }
  };
  return (
    <form onSubmit={request} className="pav-card max-w-lg space-y-3" data-testid="password-tab">
      <h2 className="font-display font-bold text-lg">Update password</h2>
      <input type="password" className="pav-input" placeholder="Current password" data-testid="pwd-current"
        value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required />
      <input type="password" className="pav-input" placeholder="New password" data-testid="pwd-new"
        value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required />
      <input type="password" className="pav-input" placeholder="Confirm new password" data-testid="pwd-confirm"
        value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} required />
      <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="pwd-request-otp">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Send OTP to confirm
      </button>
      <OtpDialog open={otpOpen} onClose={() => setOtpOpen(false)} onSubmit={verify}
        title="Confirm password change" label="A 6-digit reset code was emailed to you." />
    </form>
  );
};

// ---------- Account Info Tab ----------
const AccountInfoTab = () => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", address: "", tin: "", phone: "" });
  const [otpOpen, setOtpOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", address: user.address || "", tin: user.tin || "", phone: user.phone || "" });
  }, [user]);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/me/request-info-otp"); toast.success("Code sent — check your email"); setOtpOpen(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const verify = async (code) => {
    try {
      const { data } = await api.post("/me/update-info", { code, ...form });
      setUser(data.user);
      toast.success("Information updated");
      setOtpOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid code"); }
  };

  return (
    <form onSubmit={request} className="pav-card max-w-lg space-y-3" data-testid="info-tab">
      <h2 className="font-display font-bold text-lg">Account information</h2>
      <input className="pav-input" placeholder="Full name" data-testid="info-name"
        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      <input className="pav-input" placeholder="Address" data-testid="info-address"
        value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      <input className="pav-input" placeholder="TIN number (optional)" data-testid="info-tin"
        value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} />
      <input className="pav-input" placeholder="Contact number" data-testid="info-phone"
        value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="info-request-otp">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Send OTP to save
      </button>
      <OtpDialog open={otpOpen} onClose={() => setOtpOpen(false)} onSubmit={verify}
        title="Confirm information update" label="A 6-digit update code was emailed to you." />
    </form>
  );
};

// ---------- Receipt Details Tab ----------
const ReceiptDetailsTab = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ customer_name: "", address: "", tin: "" });

  const load = () => api.get("/me/receipt-details").then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ customer_name: "", address: "", tin: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ customer_name: r.customer_name, address: r.address, tin: r.tin || "" }); setOpen(true); };
  const save = async () => {
    if (!form.customer_name || !form.address) { toast.error("Name and address required"); return; }
    try {
      if (editing) await api.patch(`/me/receipt-details/${editing.rd_id}`, form);
      else await api.post("/me/receipt-details", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.customer_name}"?`)) return;
    try { await api.delete(`/me/receipt-details/${r.rd_id}`); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="receipt-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display font-bold text-lg">Receipt customer details</h2>
        <button onClick={openNew} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="receipt-add">
          <Plus className="h-4 w-4" /> Add new
        </button>
      </div>
      {items.length === 0 ? (
        <p className="pav-card text-center text-neutral-500 text-sm py-8">No saved receipt details yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((r) => (
            <div key={r.rd_id} className="pav-card" data-testid={`receipt-row-${r.rd_id}`}>
              <p className="font-semibold">{r.customer_name}</p>
              <p className="text-sm text-neutral-600 mt-1">{r.address}</p>
              {r.tin && <p className="text-xs text-neutral-500 mt-1">TIN: {r.tin}</p>}
              <div className="mt-3 flex gap-2">
                <button onClick={() => openEdit(r)} className="text-xs btn-outline inline-flex items-center gap-1" data-testid={`receipt-edit-${r.rd_id}`}><Pencil className="h-3 w-3" /> Edit</button>
                <button onClick={() => remove(r)} className="text-xs text-red-600 inline-flex items-center gap-1 hover:underline" data-testid={`receipt-delete-${r.rd_id}`}><Trash2 className="h-3 w-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="receipt-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit receipt details" : "New receipt details"}</DialogTitle></DialogHeader>
          <input className="pav-input" placeholder="Customer name" data-testid="rd-name"
            value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <input className="pav-input" placeholder="Address" data-testid="rd-address"
            value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <input className="pav-input" placeholder="TIN number (optional)" data-testid="rd-tin"
            value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} />
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="rd-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Addresses Tab ----------
const AddressesTab = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { receiver_name: "", contact: "", province: null, city: null, barangay: null, address_line1: "", address_line2: "", label: "" };
  const [form, setForm] = useState(blank);

  const load = () => api.get("/me/addresses").then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setOpen(true); };
  const save = async () => {
    if (!form.receiver_name || !form.contact || !form.province?.name || !form.city?.name || !form.barangay?.name || !form.address_line1) {
      toast.error("Complete all required fields"); return;
    }
    try {
      if (editing) await api.patch(`/me/addresses/${editing.addr_id}`, form);
      else await api.post("/me/addresses", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete address?`)) return;
    try { await api.delete(`/me/addresses/${a.addr_id}`); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="addresses-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display font-bold text-lg">Delivery addresses</h2>
        <button onClick={openNew} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="address-add">
          <Plus className="h-4 w-4" /> Add address
        </button>
      </div>
      {items.length === 0 ? (
        <p className="pav-card text-center text-neutral-500 text-sm py-8">No saved addresses yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((a) => (
            <div key={a.addr_id} className="pav-card" data-testid={`address-row-${a.addr_id}`}>
              {a.label && <p className="text-xs uppercase tracking-wider text-[#06B6D4] font-bold mb-1">{a.label}</p>}
              <p className="font-semibold">{a.receiver_name} <span className="text-xs text-neutral-500 font-normal">· {a.contact}</span></p>
              <p className="text-sm text-neutral-600 mt-1">
                {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ""}, {a.barangay?.name}, {a.city?.name}, {a.province?.name}
              </p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => openEdit(a)} className="text-xs btn-outline inline-flex items-center gap-1" data-testid={`address-edit-${a.addr_id}`}><Pencil className="h-3 w-3" /> Edit</button>
                <button onClick={() => remove(a)} className="text-xs text-red-600 inline-flex items-center gap-1 hover:underline" data-testid={`address-delete-${a.addr_id}`}><Trash2 className="h-3 w-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="address-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit address" : "New address"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className="pav-input" placeholder="Label (e.g. Home, Office)" data-testid="addr-label"
              value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            <input className="pav-input" placeholder="Receiver name" data-testid="addr-name"
              value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })} />
            <input className="pav-input sm:col-span-2" placeholder="Contact number" data-testid="addr-contact"
              value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
            <PSGCAddressDropdowns
              value={{ province: form.province, city: form.city, barangay: form.barangay }}
              onChange={(v) => setForm({ ...form, ...v })}
              onNonCamSurSelected={() => toast.error("Heads up: we only deliver within Camarines Sur")}
              dataTestidPrefix="addr-psgc" />
            <input className="pav-input sm:col-span-2" placeholder="Address line 1 (street, house no.)" data-testid="addr-line1"
              value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} />
            <input className="pav-input sm:col-span-2" placeholder="Address line 2 (landmark, optional)" data-testid="addr-line2"
              value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="addr-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- ProfilePage shell ----------
import { useLocation } from "react-router-dom";
const ProfilePage = () => {
  const location = useLocation();
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const validTabs = ["password", "info", "receipt", "addresses"];
  const defaultTab = validTabs.includes(requestedTab) ? requestedTab : "password";
  return (
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pav-fade-in" data-testid="profile-page">
    <div className="mb-8">
      <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">My account</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0B1E3F] mt-2">My profile</h1>
      <p className="text-neutral-600 mt-1.5 text-sm">Manage your password, account information, receipt details, and delivery addresses.</p>
    </div>
    <Tabs defaultValue={defaultTab}>
      <TabsList className="bg-[#F4F6FB] mb-5">
        <TabsTrigger value="password" data-testid="tab-password"><Lock className="h-3.5 w-3.5 mr-1.5" /> Password</TabsTrigger>
        <TabsTrigger value="info" data-testid="tab-info"><User className="h-3.5 w-3.5 mr-1.5" /> Account info</TabsTrigger>
        <TabsTrigger value="receipt" data-testid="tab-receipt"><Receipt className="h-3.5 w-3.5 mr-1.5" /> Receipt details</TabsTrigger>
        <TabsTrigger value="addresses" data-testid="tab-addresses"><MapPin className="h-3.5 w-3.5 mr-1.5" /> Addresses</TabsTrigger>
      </TabsList>
      <TabsContent value="password"><PasswordTab /></TabsContent>
      <TabsContent value="info"><AccountInfoTab /></TabsContent>
      <TabsContent value="receipt"><ReceiptDetailsTab /></TabsContent>
      <TabsContent value="addresses"><AddressesTab /></TabsContent>
    </Tabs>
  </div>
  );
};

export default ProfilePage;
